import { Dispatch, FormEvent, SetStateAction, useContext, useEffect, useState } from "react";

import { TemplateStepConstants } from "../../../../../../../constants";
import { StepModalData } from "../../../../../../../models";
import { ServiceError, Status } from "../../../../../../../service/Shared";
import { addOrRemoveWorkflowStep } from "../../../../../../../service/workflow/WorkflowService.full";
import { useIsLoadingWrapper } from "../../../../../../../utils";
import { Toast } from "../../../../../../../widget";
import { TasksBoardContext } from "../../TasksBoardContext";

interface UseAddStepModalProps {
  closeModal: () => void;
  workflowUuid?: string;
}

interface UseAddStepModalReturnData {
  step: StepModalData;
  setStep: Dispatch<SetStateAction<StepModalData>>;

  handleAddStep: (e: FormEvent<HTMLFormElement>) => void;
  isHandleAddStepLoading: boolean;
  onCancel: () => void;
  errors: ServiceError[];
}

export const useAddStepModal = ({ closeModal, workflowUuid }: UseAddStepModalProps): UseAddStepModalReturnData => {
  const { shouldRefreshTasksBoard, selectedWorkflowStage, workflowStageOptions } = useContext(TasksBoardContext);
  const [isHandleAddStepLoading, setIsHandleAddStepLoading] = useState(false);
  const [errors, setErrors] = useState<ServiceError[]>([]);
  const defaultStage = selectedWorkflowStage ?? (workflowStageOptions[0]?.key as string | undefined) ?? null;

  const [step, setStep] = useState<StepModalData>({
    ...TemplateStepConstants.DEFAULT_ADD_STEP,
    phase: defaultStage,
  });

  const onCancel = (): void => {
    setErrors([]);
    setStep({
      ...TemplateStepConstants.DEFAULT_ADD_STEP,
      phase: defaultStage,
    });
    closeModal();
  };

  const handleAddStep = useIsLoadingWrapper(async (e: FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    setErrors([]);

    if (!workflowUuid) {
      setErrors([
        {
          code: "NO_WORKFLOW",
          message: "A workflow must be selected",
        },
      ]);
    } else {
      const response = await addOrRemoveWorkflowStep({
        stepName: step.stepName,
        phase: step.phase as string,
        isRepeatable: step.isRepeatable,
        workflowUuid,
        operation: TemplateStepConstants.ADD_OPERATION,
        workflowStepUuid: null,
        workflowStepRowVersion: null,
      });
      if (response.status === Status.Success) {
        Toast.success({ message: "Step successfully added" });
        shouldRefreshTasksBoard();
        onCancel();
      }
      if (response.status === Status.Error && response.errors) {
        setErrors(response.errors);
      }
    }
  }, setIsHandleAddStepLoading);

  useEffect(() => {
    setStep((prevStep) => ({
      ...prevStep,
      phase: defaultStage,
    }));
  }, [defaultStage]);

  return { step, setStep, isHandleAddStepLoading, handleAddStep, onCancel, errors };
};
